import { IBulkPriceChange, IPriceSnapshot } from '@/interfaces/ICatalogV2';
import { IItem } from '@/interfaces/IItem';
import { formatMoneyInput, parseMoneyInput } from '@/domain/store-setup/money';
import { hasSizes, sortedSizes } from './catalog-filters';

/**
 * «Cambiar precio» from a row (plan §5.9): one line per alive size (or one
 * «Precio»), with a «Precio con descuento» field on the lines that already
 * have a discount. Only the lines the owner changed are sent, so stored
 * invalid discounts on other sizes never block the save.
 */
export interface IQuickPriceLine {
  /** 'price' or `size:{id}`. */
  key: string;
  sizeId: number | null;
  /** The size title; null for the single price. */
  label: string | null;
  paused: boolean;
  priceText: string;
  /** null = no discount field on this line. */
  discountText: string | null;
  originalPrice: number;
  originalDiscount: number | null;
}

export type TPriceFieldError = 'required' | 'invalid' | 'discountTooHigh';

export interface IQuickPriceLineErrors {
  price?: TPriceFieldError;
  discount?: TPriceFieldError;
}

export function quickPriceLines(
  item: IItem,
  country: string
): IQuickPriceLine[] {
  const text = (value: number) => formatMoneyInput(Number(value), country);

  if (hasSizes(item))
    return sortedSizes(item).map((size) => ({
      key: `size:${size.id}`,
      sizeId: size.id ?? null,
      label: size.title,
      paused: !size.active,
      priceText: text(size.cost),
      discountText:
        size.discountedCost != null ? text(size.discountedCost) : null,
      originalPrice: Number(size.cost),
      originalDiscount:
        size.discountedCost != null ? Number(size.discountedCost) : null,
    }));

  return [
    {
      key: 'price',
      sizeId: null,
      label: null,
      paused: false,
      priceText: text(item.price),
      discountText:
        item.discountedPrice != null ? text(item.discountedPrice) : null,
      originalPrice: Number(item.price),
      originalDiscount:
        item.discountedPrice != null ? Number(item.discountedPrice) : null,
    },
  ];
}

function readAmount(text: string): number | null {
  return parseMoneyInput(text, { allowZero: true });
}

function amountError(text: string): TPriceFieldError | undefined {
  if (!text.trim()) return 'required';
  return readAmount(text) === null ? 'invalid' : undefined;
}

function lineChanged(line: IQuickPriceLine): boolean {
  return (
    readAmount(line.priceText) !== line.originalPrice ||
    (line.discountText !== null &&
      readAmount(line.discountText) !== line.originalDiscount)
  );
}

/** Errors by line key; an empty object when the form can be saved. */
export function validateQuickPrice(
  lines: readonly IQuickPriceLine[]
): Record<string, IQuickPriceLineErrors> {
  const errors: Record<string, IQuickPriceLineErrors> = {};
  for (const line of lines) {
    const lineErrors: IQuickPriceLineErrors = {};
    const priceError = amountError(line.priceText);
    if (priceError) lineErrors.price = priceError;

    if (line.discountText !== null) {
      const discountError = amountError(line.discountText);
      const price = readAmount(line.priceText);
      const discount = readAmount(line.discountText);
      if (discountError) lineErrors.discount = discountError;
      else if (
        price !== null &&
        discount !== null &&
        discount >= price &&
        lineChanged(line)
      )
        lineErrors.discount = 'discountTooHigh';
    }

    if (lineErrors.price || lineErrors.discount) errors[line.key] = lineErrors;
  }
  return errors;
}

/** The `bulk/prices` change of a valid form; null when nothing changed. */
export function quickPriceChange(
  item: IItem,
  lines: readonly IQuickPriceLine[]
): IBulkPriceChange | null {
  if (Object.keys(validateQuickPrice(lines)).length) return null;
  const changed: Array<{
    line: IQuickPriceLine;
    price: number;
    discount: number | undefined;
  }> = [];
  for (const line of lines) {
    const price = readAmount(line.priceText);
    const discount =
      line.discountText !== null ? readAmount(line.discountText) : undefined;
    if (price !== null && discount !== null && lineChanged(line))
      changed.push({ line, price, discount });
  }
  if (!changed.length) return null;

  if (hasSizes(item))
    return {
      itemId: item.id,
      sizes: changed.flatMap(({ line, price, discount }) =>
        line.sizeId === null
          ? []
          : [
              {
                id: line.sizeId,
                cost: price,
                ...(discount !== undefined ? { discountedCost: discount } : {}),
              },
            ]
      ),
    };

  const [{ price, discount }] = changed;
  return {
    itemId: item.id,
    price,
    ...(discount !== undefined ? { discountedPrice: discount } : {}),
  };
}

/** The item's prices as the API snapshots them (the `ifMatch` of an undo). */
export function priceSnapshotOf(item: IItem): IPriceSnapshot {
  return {
    price: Number(item.price),
    discountedPrice:
      item.discountedPrice != null ? Number(item.discountedPrice) : null,
    sizes: hasSizes(item)
      ? sortedSizes(item).flatMap((size) =>
          size.id === undefined
            ? []
            : [
                {
                  id: size.id,
                  cost: Number(size.cost),
                  discountedCost:
                    size.discountedCost != null
                      ? Number(size.discountedCost)
                      : null,
                },
              ]
        )
      : [],
  };
}

/**
 * The undo of a price change: the `before` values, applied only while the
 * product still has what the action wrote (another device's change is kept).
 */
export function undoPriceChange(
  before: IPriceSnapshot & { itemId: number },
  after: IItem
): IBulkPriceChange {
  const ifMatch = priceSnapshotOf(after);
  if (before.sizes.length)
    return {
      itemId: before.itemId,
      sizes: before.sizes.map((size) => ({
        id: size.id,
        cost: size.cost,
        discountedCost: size.discountedCost,
      })),
      ifMatch,
    };
  return {
    itemId: before.itemId,
    price: before.price,
    discountedPrice: before.discountedPrice,
    ifMatch,
  };
}
